import { Button, Pagination } from "@mui/material"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { useNavigate } from "react-router-dom"
import { TableHeaderItem } from "src/components/mypage/TableHeaderItem"
import { SNACKBAR_TYPES, SORT_ORDER } from "src/modules/const"
import { Event, EventService } from "src/openapi"
import { EventItem } from "src/pages/event/components/EventItem"
import { Box, Grid, Typography } from "src/UILibrary"
import { ApplicationTableItem } from "./ApplicationTableItem"
import { EventEditPage } from "./EventEditPage"
import { EventUserDetailPage } from "./EventUserDetailPage"
import { useLoginContext } from "src/hooks/useLoginContext"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"

type Props = {
  item: Event
  closeDetail: any
}

const SORT_BY = {
  DATE: "created_at",
  STATUS: "status",
  NAME: "name",
  JOIN_METHOD: "join_method",
}

const PAGE_SIZE = 10

export const EventDetailPage = ({ item, closeDetail }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.event" })

  const isPC = useMediaQuery({ query: "(min-width: 1400px)" })
  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  const { accessToken, checkLoginStatus } = useLoginContext()
  const navigate = useNavigate()

  const [, setShowToast] = useRecoilState(ShowToast)

  const [applications, setApplications] = useState<[]>([])
  const [pageNum, setPageNum] = useState<number>(1)
  const [total] = useState<number>(item.eventApplications?.length as number)
  const [currentSort, setCurrentSort] = useState<string>(SORT_BY.DATE)
  const [currentSortOrder, setCurrentSortOrder] = useState<string>(SORT_ORDER.DESCENDONG)

  const [selectedApplication, setSelectedApplication] = useState<any>()

  const [openDetail, setOpenDetail] = useState<boolean>(false)

  let headers: any[] = [
    {
      size: isPC ? 2 : isTablet ? 2 : 1,
      name: t("date"),
      sortby: SORT_BY.DATE,
    },
    {
      size: isPC ? 2 : isTablet ? 3 : 1,
      name: t("status"),
      sortby: SORT_BY.STATUS,
    },
    {
      size: isPC ? 2 : isTablet ? 3 : 1,
      name: t("member_name"),
      sortby: SORT_BY.NAME,
    },
    {
      size: isPC ? 6 : isTablet ? 3 : 1,
      name: t("join_method"),
      sortby: SORT_BY.JOIN_METHOD,
    },
  ]

  const getApplication = (sort: string, sortOrder: string) => {
    let applications = item.eventApplications
    applications?.sort((a: any, b: any) => {
      switch (sort) {
        case SORT_BY.DATE:
          if (sortOrder === SORT_ORDER.ASCENDING) {
            return a.application?.createdAt > b.application?.createdAt ? 1 : -1
          } else {
            return a.application?.createdAt < b.application?.createdAt ? 1 : -1
          }
        case SORT_BY.STATUS:
          if (sortOrder === SORT_ORDER.ASCENDING) {
            return a.application.status > b.application.status ? 1 : -1
          } else {
            return a.application.status < b.application.status ? 1 : -1
          }
        case SORT_BY.NAME:
          if (sortOrder === SORT_ORDER.ASCENDING) {
            return a.user.lastName > b.user.lastName ? 1 : -1
          } else {
            return a.user.lastName < b.user.lastName ? 1 : -1
          }
        case SORT_BY.JOIN_METHOD:
          if (sortOrder === SORT_ORDER.ASCENDING) {
            return a.application.type > b.application.type ? 1 : -1
          } else {
            return a.application.type < b.application.type ? 1 : -1
          }
      }
      return 1
    })

    setApplications(applications?.slice((pageNum - 1) * PAGE_SIZE, pageNum * PAGE_SIZE) as [])
  }

  const handleCSVExport = () => {
    EventService.exportEventUserList(item.id!, accessToken)
      .then((res) => {
        const encoding = require("encoding-japanese")
        const str_array = encoding.stringToCode(res.toString())
        const sjis_array = encoding.convert(str_array, "SJIS", "UNICODE")
        const uint8_array = new Uint8Array(sjis_array)

        const blob = new Blob([uint8_array], {
          type: "text/csv",
        })
        const url = window.URL.createObjectURL(blob)

        const a = document.createElement("a")
        a.href = url
        a.download = "event_application_user_list.csv"
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        window.URL.revokeObjectURL(url)
      })
      .catch((err) => {
        if (!checkLoginStatus(err)) {
          return
        }

        setShowToast({ show: true, text: t("failed_export_csv"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const onSortChanged = (sorter: string, asc: boolean) => {
    getApplication(sorter, asc ? SORT_ORDER.ASCENDING : SORT_ORDER.DESCENDONG)
    setCurrentSort(sorter)
    setCurrentSortOrder(asc ? SORT_ORDER.ASCENDING : SORT_ORDER.DESCENDONG)
  }

  useEffect(() => {
    getApplication(currentSort, currentSortOrder)
  }, [pageNum])

  const handleNewEvent = () => {
    navigate("/mypage/event/create")
    navigate(0)
  }

  if (openDetail) {
    return <EventEditPage item={item} closeDetail={() => setOpenDetail(false)} />
  }

  if (selectedApplication) {
    return (
      <EventUserDetailPage
        event={item}
        item={selectedApplication}
        closeDetail={() => setSelectedApplication(undefined)}
      />
    )
  }

  return (
    <Box
      sx={{
        ...(isTablet
          ? { height: "auto", pt: "0px", pl: "20px" }
          : { width: "100%", position: "absolute", top: 60, bottom: 120 }),
        overflowY: "scroll",
      }}
    >
      <Typography.FileFieldDetail
        sx={{
          fontFamily: "Noto Sans JP !important",
          my: "12px",
          ml: "20px",
          ":hover": { opacity: 0.8, cursor: "pointer" },
        }}
        onClick={() => closeDetail()}
      >
        {"＜　"}
        {t("page_back")}
      </Typography.FileFieldDetail>
      <Box
        justifyContent={isTablet ? "unset" : "space-between"}
        sx={{
          display: "flex",
          width: "100%",
          pl: "20px",
          py: "10px",
          borderTop: "1px dashed #D4D3D7",
          borderBottom: "1px dashed #D4D3D7",
        }}
      >
        <Typography.InputFieldTitle
          sx={{
            my: "auto",
            mr: isTablet ? "auto" : "0px",
            fontWeight: "700",
            fontFamily: "Noto Sans JP !important",
          }}
        >
          {t("event_data")}
        </Typography.InputFieldTitle>
        <Box sx={{ display: "flex" }}>
          <Button
            variant="outlined"
            sx={{
              minWidth: isTablet ? "200px" : "80px",
              fontSize: isTablet ? "1rem" : "0.8rem",
              fontFamily: "Noto Sans JP !important",
              lineHeight: isTablet ? "1rem" : "0.9rem",
              fontWeight: 700,
              mr: isTablet ? "10px" : "5px",
              borderRadius: 0,
            }}
            onClick={() => handleCSVExport()}
          >
            {t("csv_export")}
          </Button>
          <Button
            variant="contained"
            sx={{
              minWidth: isTablet ? "240px" : "120px",
              fontSize: isTablet ? "1rem" : "0.8rem",
              fontFamily: "Noto Sans JP !important",
              lineHeight: isTablet ? "1rem" : "0.9rem",
              fontWeight: 700,
              mr: isTablet ? "0px" : "10px",

              justifyContent: "center",
              py: "16px",
              borderRadius: 0,
              ":hover": {
                bgcolor: "#2DA389",
              },
            }}
            onClick={() => handleNewEvent()}
          >
            {t("new_event")}
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          pl: "20px",
          py: "10px",
        }}
      >
        <EventItem
          item={item}
          handleSelected={() => {
            setOpenDetail(true)
          }}
          clickable
          showBehalfJoinCnt
        />
      </Box>

      {isTablet ? (
        <Grid container sx={{ mt: "0px", height: "32px", borderBottom: "1px dotted #D4D3D7" }}>
          {headers.map((header, index) => (
            <TableHeaderItem
              key={index}
              name={header.name}
              size={header.size}
              sortby={header.sortby}
              onSortChanged={onSortChanged}
            />
          ))}
        </Grid>
      ) : (
        <></>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "auto",
        }}
      >
        {applications.length === 0 ? (
          <Typography.SubTitleNormal
            sx={{ my: "50px", mx: "auto", fontFamily: "Noto Sans JP !important", color: "gray" }}
          >
            {t("no_data")}
          </Typography.SubTitleNormal>
        ) : (
          <>
            {applications.map((application, index) => (
              <ApplicationTableItem
                key={index}
                item={application}
                handleSelected={setSelectedApplication}
              />
            ))}
            <Box display="flex" sx={{ my: "20px", width: "100%" }}>
              <Pagination
                count={Math.ceil(total / PAGE_SIZE)}
                variant="outlined"
                color="primary"
                onChange={(event, page) => setPageNum(page)}
                sx={{ mx: "auto" }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
